import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, CalendarRange, Clock, Images, Package, Tag, FileText, Users, Crown, LogOut, Menu, X, Star, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin/calendar", label: "Calendar", icon: CalendarRange },
  { to: "/admin", label: "Bookings", icon: LayoutDashboard, end: true },
  { to: "/admin/availability", label: "Availability", icon: CalendarDays },
  { to: "/admin/studio-hours", label: "Studio Hours", icon: Clock },
  { to: "/admin/content", label: "Content", icon: Images },
  { to: "/admin/equipment", label: "Equipment", icon: Package, superOnly: true },
  { to: "/admin/promos", label: "Promos", icon: Tag, superOnly: true },
  { to: "/admin/text-messages", label: "Text Messages", icon: MessageSquare, superOnly: true },
  { to: "/admin/about", label: "About", icon: FileText },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = (
    <nav className="flex flex-col gap-1">
      {LINKS.filter((l) => !l.superOnly || admin?.role === "super_admin").map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          onClick={() => setOpen(false)}
          data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#D4AF37] text-black font-medium" : "text-white/70 hover:bg-white/5 hover:text-white"}`
          }
        >
          <l.icon size={17} /> {l.label}
        </NavLink>
      ))}
      {admin?.role === "super_admin" && (
        <NavLink
          to="/admin/super"
          onClick={() => setOpen(false)}
          data-testid="admin-nav-super"
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-[#D4AF37] text-black font-medium" : "text-[#D4AF37] hover:bg-white/5"}`
          }
        >
          <Crown size={17} /> Super Admin
        </NavLink>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-layout">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0a] p-4 fixed inset-y-0">
        <Link to="/" className="mb-8 px-2"><img src="/xmd-logo.png" alt="XMD" className="h-9" /></Link>
        {nav}
        <div className="mt-auto pt-4 border-t border-white/10">
          <p className="px-4 text-xs text-white/40 truncate">{admin?.email}</p>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-2.5 mt-1 rounded-lg text-sm text-white/70 hover:text-red-400 w-full">
            <LogOut size={17} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 glass h-14 flex items-center justify-between px-4">
        <img src="/xmd-logo.png" alt="XMD" className="h-7" />
        <button onClick={() => setOpen((o) => !o)} data-testid="admin-mobile-toggle" className="text-white">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/80 pt-14" onClick={() => setOpen(false)}>
          <div className="glass p-4 m-3 rounded-2xl" onClick={(e) => e.stopPropagation()}>
            {nav}
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-3 px-4 py-2.5 mt-2 rounded-lg text-sm text-white/70 hover:text-red-400 w-full border-t border-white/10">
              <LogOut size={17} /> Logout
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 md:ml-60 p-5 md:p-8 pt-20 md:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
